import { useState, useRef, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useDemographics } from "~/lib/demographics-state"
import { demographicFields, type DemographicField } from "~/data/demographics"

const inputClass =
  "w-full rounded-lg border-2 border-core-mid bg-core-gray/50 px-4 py-3 text-white placeholder-white/40 outline-none transition-colors focus:border-core-orange"

function MultiSelectDropdown({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selected = value ? value.split(",").filter(Boolean) : []

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  function toggle(optValue: string) {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue]
    onChange(next.join(","))
  }

  const displayText =
    selected.length > 0
      ? options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(", ")
      : placeholder

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${inputClass} text-left flex items-center justify-between ${
          selected.length === 0 ? "text-white/40" : ""
        }`}
      >
        <span className="truncate">{displayText}</span>
        <svg
          className="w-4 h-4 ml-2 shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border-2 border-core-mid bg-core-black shadow-lg max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-white hover:bg-core-gray/50 transition-colors"
            >
              <span
                className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                  selected.includes(opt.value)
                    ? "border-core-orange bg-core-orange"
                    : "border-core-mid"
                }`}
              >
                {selected.includes(opt.value) && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: DemographicField
  value: string
  onChange: (value: string) => void
}) {
  if (field.type === "multi-select") {
    return (
      <MultiSelectDropdown
        options={field.options ?? []}
        value={value}
        onChange={onChange}
        placeholder={`Select ${field.label.toLowerCase()}...`}
      />
    )
  }

  if (field.type === "select") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} ${!value ? "text-white/40" : ""}`}
      >
        <option value="">Select {field.label.toLowerCase()}...</option>
        {field.options?.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    )
  }

  return (
    <input
      type={field.type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={inputClass}
    />
  )
}

export function DemographicsForm() {
  const { values, setValue, isComplete, hydrated } = useDemographics()
  const navigate = useNavigate()

  if (!hydrated) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-white/50">Loading...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <h2 className="text-3xl text-core-orange mb-2">Before We Begin</h2>
        <p className="text-white/60 mb-8">
          Tell us a little about yourself.
        </p>

        <div className="flex flex-col gap-5">
          {demographicFields.map((field) => (
            <div key={field.id}>
              <label className="block text-sm text-white/70 mb-1.5">
                {field.label}
                {field.required && <span className="text-core-orange ml-1">*</span>}
                {field.type === "multi-select" && (
                  <span className="text-white/40 ml-1">(select all that apply)</span>
                )}
              </label>
              <FieldInput
                field={field}
                value={values[field.id] ?? ""}
                onChange={(v) => setValue(field.id, v)}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => navigate({ to: "/survey" })}
          disabled={!isComplete}
          className="mt-10 w-full rounded-full bg-core-orange px-8 py-3 text-white font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
